import React, { useEffect, useRef, useState } from 'react';
import WebAppzAPI from './API/WebAppzAPI';
import ShopCard from './Components/Cards/ShopCard';
import LoadingBlock from './Components/Blocks/LoadingBlock';

import Settings from './Comps/Control/Settings';

import WelcomePage from './Pages/WelcomePage';

import { useTranslation } from 'react-i18next';
import './i18n';

import './Assets/Css/style.css';
import { syncTelegramBottomButtons } from './Utils/telegramBottomButtons';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Alert } from '@mui/material';

const InvoiceView = React.lazy(() => import('./Invoice/InvoiceView'));
const DisplayWallet = React.lazy(() => import('./Components/TelegramWallet/WalletInfo'));

const CONNECT_BUILD_ID = '20260806-telegram-startup-retry-v1';

const waitForTelegramUser = async (timeoutMs = 8000, intervalMs = 100) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < timeoutMs) {
        const WebApp = window.Telegram?.WebApp;
        const telegramUser = WebApp?.initDataUnsafe?.user;

        if (telegramUser?.id) {
            return { WebApp, telegramUser };
        }

        await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
    }

    return {
        WebApp: window.Telegram?.WebApp,
        telegramUser: null,
    };
};

const createBootstrapUser = () => {
    const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user || {};

    return {
        userid: String(telegramUser.id || ''),
        first_name: String(telegramUser.first_name || ''),
        last_name: String(telegramUser.last_name || ''),
        username: telegramUser.username === undefined ? null : String(telegramUser.username),
        balance: 0,
        language_code: telegramUser.language_code || 'en',
        WalletAddress: '',
        firstLevelRefs: [],
        secondLevelRefs: [],
        shop_answer: null,
    };
};

const App = () => {
    const { t, i18n } = useTranslation();
    const [user, setUser] = useState(null);
    const [shops, setShops] = useState(null);
    const [refs, setRefs] = useState(null);
    const [isFetched, setIsFetched] = useState(false);
    const [page, setPage] = useState('welcome');
    const [openChannelDialog, setOpenChannelDialog] = useState(false);
    const [openShopNameDialog, setOpenShopNameDialog] = useState(false);
    const [channelLink, setChannelLink] = useState('');
    const [shopName, setShopName] = useState('');
    const [error, setError] = useState('');
    const [openCreateShopDialog, setOpenCreateShopDialog] = useState(false);
    const [loadingCreateShop, setLoadingCreateShop] = useState(false);
    const [startupError, setStartupError] = useState('');
    const [shopsLoaded, setShopsLoaded] = useState(false);
    const [shopsLoading, setShopsLoading] = useState(false);
    const shopsRequestRef = useRef(null);
    const bootstrapUserRef = useRef(null);

    if (bootstrapUserRef.current === null) {
        bootstrapUserRef.current = createBootstrapUser();
    }

    const authenticateBiometric = async () => {
        try {

            const initBiometricManager = () => {
                return new Promise((resolve, reject) => {
                    window.Telegram.WebApp.BiometricManager.init(() => {
                        if (window.Telegram.WebApp.BiometricManager.isInited) {
                            resolve();
                        } else {
                            reject(false);  // Возвращаем false, если не удалось инициализировать
                        }
                    });
                });
            };

            // Превращаем запрос доступа в промис
            const requestBiometricAccess = () => {
                return new Promise((resolve, reject) => {
                    window.Telegram.WebApp.BiometricManager.requestAccess({}, (isAccessGranted) => {
                        if (isAccessGranted) {
                            resolve();
                        } else {
                            reject(false);  // Возвращаем false, если доступ отклонен
                        }
                    });
                });
            };

            // Превращаем аутентификацию в промис
            const authenticateUser = () => {
                return new Promise((resolve, reject) => {
                    window.Telegram.WebApp.BiometricManager.authenticate({}, (isAuthenticated) => {
                        if (isAuthenticated) {
                            resolve(true);  // Возвращаем true, если аутентификация успешна
                        } else {
                            reject(false);  // Возвращаем false, если аутентификация не удалась
                        }
                    });
                });
            };

            await initBiometricManager();
            await requestBiometricAccess();
            const result = await authenticateUser();

            return result;

        } catch (error) {
            console.error("Ошибка биометрической аутентификации:", error);
            return false;  // Возвращаем false, если произошла ошибка на любом этапе
        }
    }

    const [openModalShops, setOpenModalShops] = useState(false);
    const [loading, setLoading] = useState(true);

    React.useEffect(() => {
        const WebApp = window.Telegram?.WebApp;
        if (!WebApp?.SecondaryButton) {
            return undefined;
        }

        const syncButtons = () => syncTelegramBottomButtons(WebApp, window);
        syncButtons();

        window.addEventListener('resize', syncButtons);
        window.visualViewport?.addEventListener('resize', syncButtons);
        WebApp.onEvent?.('viewportChanged', syncButtons);
        WebApp.onEvent?.('safeAreaChanged', syncButtons);
        WebApp.onEvent?.('contentSafeAreaChanged', syncButtons);

        return () => {
            window.removeEventListener('resize', syncButtons);
            window.visualViewport?.removeEventListener('resize', syncButtons);
            WebApp.offEvent?.('viewportChanged', syncButtons);
            WebApp.offEvent?.('safeAreaChanged', syncButtons);
            WebApp.offEvent?.('contentSafeAreaChanged', syncButtons);
        };
    }, []);

    React.useEffect(() => {
        if (!user) {
            return;
        }

        const WebApp = window.Telegram?.WebApp;
        const startup = window.__WEBAPPZ_CONNECT_STARTUP__;
        if (!startup) {
            return;
        }

        if (startup.mainHandler) {
            WebApp?.MainButton?.offClick(startup.mainHandler);
        }
        if (startup.secondaryHandler) {
            WebApp?.SecondaryButton?.offClick(startup.secondaryHandler);
        }

        if (startup.pendingCreate && user.invoice === undefined && user.connectwallet === undefined) {
            startup.pendingCreate = false;
            setOpenChannelDialog(true);
        }

        startup.released = true;
    }, [user]);

    useEffect(() => {
        if (isFetched) {
            return;
        }

        setIsFetched(true);

        const fetchData = async () => {
            window.Telegram?.WebApp?.ready?.();
            const { WebApp, telegramUser } = await waitForTelegramUser();

            WebApp?.ready?.();

            try {
                if (!telegramUser) {
                    throw new Error('Telegram user data is unavailable');
                }

                let userData = {
                    userid: `${telegramUser.id}`,
                    first_name: `${telegramUser.first_name || ''}`,
                    last_name: `${telegramUser.last_name || ''}`,
                    username: `${telegramUser.username === undefined ? null : telegramUser.username}`,
                    refID: '0',
                    defer_shops: '1',
                };

                if(WebApp.initDataUnsafe.start_param !== undefined) {
                    const params = WebApp.initDataUnsafe.start_param.split('|');
                    params.some(param => {
                        const match = param.match(/ref(\d+)/);
                        if (match) {
                            userData.refID = match[1];
                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const match = param.match(/invoice(\w+)/);
                        if (match) {
                            userData.invoice = match[1];
                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const uidMatch = param.match(/shopid([A-Za-z0-9]+)/);
                        const numericMatch = param.match(/shop(\d+)/);
                        const match = uidMatch || numericMatch;
                        if (match) {
                            userData.shopIDfind = match[1];
                            setOpenModalShops(true);
                            return true;
                        }
                        return false;
                    });

                    params.some(param => {
                        const match = param.match(/connectwallet/);
                        if (match) {
                            userData.connectwallet = true;
                            return true;
                        }
                        return false;
                    });
                }

                const connectData = await WebAppzAPI.OAuthConnect({ userData });
                if (!connectData || !connectData.token) {
                    throw new Error(connectData?.message || 'Invalid Connect response');
                }

                userData.userPhoto = connectData.userPhoto;
                userData.balance = connectData.balance;
                userData.token = connectData.token;
                userData.generate_images_history = connectData.generate_images_history;
                userData.firstLevelRefs = connectData.firstLevelRefs || [];
                userData.secondLevelRefs = connectData.secondLevelRefs || [];
                userData.BiometricConfirm = connectData.BiometricConfirm;
                userData.WalletAddress = connectData.WalletAddress || connectData.wallet_address || '';
                userData.shop_answer = connectData.shop_answer ?? null;

                if(connectData.oo !== undefined) {
                    userData.oo = connectData.oo;
                }

                if (userData.BiometricConfirm === 1) {
                    const authBiometricAnswere = await authenticateBiometric();
                    userData.authBiometricAnswere = authBiometricAnswere;
                }

                const languageCode = connectData.language_code || telegramUser.language_code || 'en';
                userData.language_code = languageCode;
                i18n.changeLanguage(languageCode).catch((error) => {
                    console.error('Failed to apply language:', error);
                });

                if(!connectData.language_code) {
                    WebAppzAPI.changeLanguage(languageCode, userData).catch((error) => {
                        console.error('Failed to save language:', error);
                    });
                }

                setRefs({
                    'firstLevel': userData.firstLevelRefs,
                    'secondLevel': userData.secondLevelRefs,
                });
                setShops(Array.isArray(connectData.shops) ? connectData.shops : []);
                setShopsLoaded(connectData.shops_deferred !== true);
                setUser(userData);
            } catch (error) {
                console.error('Connect startup failed:', error);
                setStartupError('Не удалось загрузить WebAppz Connect. Закройте окно и откройте его снова.');
            } finally {
                setLoading(false);
                WebApp?.ready?.();
            }
        };

        fetchData();
    }, [isFetched, i18n]);

    const loadShops = async () => {
        if (!user) {
            return [];
        }

        if (shopsLoaded) {
            return Array.isArray(shops) ? shops : [];
        }

        if (shopsRequestRef.current) {
            return shopsRequestRef.current;
        }

        setShopsLoading(true);
        shopsRequestRef.current = WebAppzAPI.getConnectShops(user)
            .then((response) => {
                const loadedShops = Array.isArray(response?.shops) ? response.shops : [];
                setShops(loadedShops);
                setShopsLoaded(true);
                return loadedShops;
            })
            .catch((error) => {
                console.error('Failed to load shops:', error);
                throw error;
            })
            .finally(() => {
                setShopsLoading(false);
                shopsRequestRef.current = null;
            });

        return shopsRequestRef.current;
    };

    React.useEffect(() => {
        if(user !== null) {
            if(page === 'welcome') {

            } else if(page === 'webappz_chat') {
                window.Telegram.WebApp.BackButton.show();
                window.Telegram.WebApp.BackButton.onClick(() => {
                    setPage('welcome');
                });
            }

            return () => {
                window.Telegram?.WebApp?.MainButton?.offClick();
                window.Telegram?.WebApp?.BackButton?.offClick();
            };
        }
    }, [page, user]);

    const sendDataToServer = async (data, user) => {
        data.userid = user.userid;
        data.token = user.token;
        await WebAppzAPI.createShopRequest(data);
    };

    React.useEffect(() => {
        const WebApp = window.Telegram?.WebApp;
        const mainButton = WebApp?.MainButton;
        const secondaryButton = WebApp?.SecondaryButton;

        if (!mainButton) {
            return;
        }

        if (user === null) {
            return;
        }

        if(user !== null && (user.authBiometricAnswere === undefined || user.authBiometricAnswere === true) && user.invoice === undefined && user.connectwallet === undefined) {
            const handleInvite = () => {
                window.location.href="https://t.me/share/url?url=https://t.me/webappzconnectbot/app?startapp=ref" + user.userid + "&text=" + t('connect_link');
            };

            if (secondaryButton) {
                secondaryButton.text = t('invite_button');
                secondaryButton.color = '#0098EA';
                secondaryButton.textColor = '#fff';
                secondaryButton.onClick(handleInvite);
            }

            if(user.shop_answer === null) {
                secondaryButton?.show();
                mainButton.show();
            }

            return () => secondaryButton?.offClick(handleInvite);
        } else if(user && user.invoice === undefined) {
            mainButton.hide();
            secondaryButton?.hide();
        } else {
            secondaryButton?.hide();
        }
    }, [t, user]);

    const [openSettings, setOpenSettings] = useState(false);

    React.useEffect(() => {
        const settingsButton = window.Telegram?.WebApp?.SettingsButton;
        if (!settingsButton) {
            return;
        }

        const toggleSettings = () => setOpenSettings((isOpen) => !isOpen);
        settingsButton.show();
        settingsButton.onClick(toggleSettings);

        return () => settingsButton.offClick(toggleSettings);
    }, []);

    const handleChannelSubmit = async () => {
        window.Telegram.WebApp.MainButton.showProgress();
        setLoadingCreateShop(true);
        if (channelLink.length < 1 || (!channelLink.startsWith('https://') && !channelLink.startsWith('@'))) {
            setError(t('incorrect_channel_link'));
            setLoadingCreateShop(false);
            return;
        }

        const data = { text: channelLink, isChannel: true };
        await sendDataToServerCreateShop(data, user);
        setOpenChannelDialog(false);
        setLoadingCreateShop(false);
        setChannelLink('');
        window.Telegram.WebApp.MainButton.hideProgress();
    };

    const handleShopSubmit = async () => {
        setLoadingCreateShop(true);
        window.Telegram.WebApp.MainButton.showProgress();
        if (shopName.length < 4 || shopName.length > 24) {
            setError(t('max_24_symbols'));
            setLoadingCreateShop(false);
            return;
        }

        const data = { text: shopName, isChannel: false };
        await sendDataToServerCreateShop(data, user);
        setOpenShopNameDialog(false);
        setShopName('');
        setLoadingCreateShop(false);
        window.Telegram.WebApp.MainButton.hideProgress();
    };

    const sendDataToServerCreateShop = async (data, user) => {
        data.userid = user.userid;
        data.token = user.token;
        const response = await WebAppzAPI.createShopRequest(data);
        if(response.status === 'success') {
            window.Telegram.WebApp.showAlert(t('shop_successfully_created'));
            window.location.href = 'https://t.me/webappzconnectbot';
            window.Telegram.WebApp.close();
        } else if(response.status === 'error') {
            window.Telegram.WebApp.showAlert(response.message);
        } else {
            window.Telegram.WebApp.showAlert("Server error");
        }
    };

    React.useEffect(() => {
        const mainButton = window.Telegram?.WebApp?.MainButton;
        const secondaryButton = window.Telegram?.WebApp?.SecondaryButton;
        if (!mainButton) {
            return;
        }

        if (user === null) {
            if (startupError) {
                mainButton.hide();
                secondaryButton?.hide();
            }
            return;
        }

        if(user !== null && user.invoice === undefined) {
            if(openSettings) {
                mainButton.hide();
                secondaryButton?.hide();
                // return <Settings openSettings={openSettings} setUser={setUser} setOpenSettings={setOpenSettings} user={user} shops={shops} refs={refs} />
            } else {
                mainButton.show();
                secondaryButton?.show();
            }
        } else {
            mainButton.hide();
            secondaryButton?.hide();
        }
    }, [openSettings, startupError, user]);

    React.useEffect(() => {
        if(user !== null && user.invoice === undefined) {
            const mainButton = window.Telegram?.WebApp?.MainButton;
            const secondaryButton = window.Telegram?.WebApp?.SecondaryButton;

            if (!mainButton) {
                return;
            }

            const handleMainButtonClick = () => {
                if(openChannelDialog && channelLink.length < 1) {
                    setOpenChannelDialog(false);
                    setOpenShopNameDialog(true);
                } else if(openChannelDialog && channelLink.length > 0) {
                    handleChannelSubmit();
                } else if(openShopNameDialog && shopName.length < 1) {
                    setOpenChannelDialog(true);
                    setOpenShopNameDialog(false);
                } else if(openShopNameDialog && shopName.length > 0) {
                    handleShopSubmit();
                } else {
                    setOpenChannelDialog(true);
                }
            }

            if(openChannelDialog && channelLink.length < 1) {
                mainButton.onClick(handleMainButtonClick);
                mainButton.text = t('i_dont_have_channel');
                secondaryButton?.hide();
            } else if(openChannelDialog && channelLink.length > 0) {
                mainButton.onClick(handleMainButtonClick);
                mainButton.text = t('create_shop');
                secondaryButton?.hide();
            } else if(openShopNameDialog && shopName.length < 1) {
                mainButton.onClick(handleMainButtonClick);
                mainButton.text = t('back');
                secondaryButton?.hide();
            } else if(openShopNameDialog && shopName.length > 0) {
                mainButton.onClick(handleMainButtonClick);
                mainButton.text = t('create_shop');
                secondaryButton?.hide();
            } else {
                mainButton.onClick(handleMainButtonClick);
                setOpenChannelDialog(false);
                setOpenShopNameDialog(false);
                mainButton.text = t('create_shop');
                secondaryButton?.show();
                mainButton.color = '#0098EA';
            }

            return () => {
                mainButton.offClick(handleMainButtonClick)
            }
        }

    }, [openCreateShopDialog,openShopNameDialog, t, shopName, openChannelDialog, channelLink, user]);

    if(startupError) {
        return (
            <div style={{padding: '24px', minHeight: '100vh', background: 'var(--tg-theme-bg-color)'}}>
                <Alert severity="error" sx={{marginBottom: '16px'}}>{startupError}</Alert>
                <Button variant="contained" onClick={() => window.location.reload()}>Повторить</Button>
            </div>
        );
    }

    if(user !== null && user.authBiometricAnswere !== undefined && user.authBiometricAnswere === false) {
        window.Telegram?.WebApp?.MainButton?.hide();
        window.Telegram?.WebApp?.SecondaryButton?.hide();
        return <LoadingBlock message={"Failed to verify biometry"} />;
    }

    const displayUser = user || bootstrapUserRef.current;
    const isRegularConnect = user === null
        || (user.invoice === undefined && user.connectwallet === undefined);

    return (
        <>
            <Dialog open={openCreateShopDialog} onClose={() => setOpenCreateShopDialog(false)}>
                <DialogTitle sx={{ background: "var(--tg-theme-bg-color)" }}>{t('do_you_have_a_telegram_channel')}</DialogTitle>
                <DialogContent sx={{ background: "var(--tg-theme-bg-color)" }}>
                    <Button onClick={() => { setOpenCreateShopDialog(false); setOpenChannelDialog(true); }}>{t('yes')}</Button>
                    <Button onClick={() => { setOpenCreateShopDialog(false); setOpenShopNameDialog(true); }}>{t('no')}</Button>
                </DialogContent>
            </Dialog>

            <Dialog open={openChannelDialog} fullScreen={true} onClose={() => setOpenChannelDialog(false)}>
                <DialogTitle sx={{ background: "var(--tg-theme-bg-color)" }}>{t('enter_channel_link')}</DialogTitle>
                <DialogContent sx={{ background: "var(--tg-theme-bg-color)" }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="channelLink"
                        label={t('linkusername_to_channel')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={channelLink}
                        onChange={(e) => setChannelLink(e.target.value)}
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                    {
                        !loadingCreateShop && (
                            <DialogActions sx={{ background: "var(--tg-theme-bg-color)" }}>
                                <Button onClick={() => setOpenChannelDialog(false)}>{t('cancel')}</Button>
                                {/*<Button onClick={handleChannelSubmit}>{t('further')}</Button>*/}
                            </DialogActions>
                        )
                    }
                </DialogContent>
            </Dialog>

            <Dialog open={openShopNameDialog} fullScreen={true} onClose={() => setOpenShopNameDialog(false)}>
                <DialogTitle sx={{ background: "var(--tg-theme-bg-color)" }}>{t('notify_max_24_symbols')}</DialogTitle>
                <DialogContent sx={{ background: "var(--tg-theme-bg-color)" }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="shopName"
                        label={t('enter_shop_name')}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={shopName}
                        onChange={(e) => setShopName(e.target.value)}
                    />
                    {error && <Alert severity="error">{error}</Alert>}
                    {
                        !loadingCreateShop && (
                            <DialogActions sx={{ background: "var(--tg-theme-bg-color)" }}>
                                <Button onClick={() => setOpenShopNameDialog(false)}>{t('cancel')}</Button>
                                {/*<Button onClick={handleShopSubmit}>{t('further')}</Button>*/}
                            </DialogActions>
                        )
                    }
                </DialogContent>
            </Dialog>

            {isRegularConnect ? (
                <>
                    {
                        user && openSettings && (
                            <Settings openSettings={openSettings} setUser={setUser} setOpenSettings={setOpenSettings} user={user} shops={shops} refs={refs} />
                        )
                    }
                    {
                        page === 'welcome' && !openSettings ? (
                            <div className="App" data-build={CONNECT_BUILD_ID}>
                                <WelcomePage
                                    user={displayUser}
                                    openModalShops={openModalShops}
                                    setOpenModalShops={setOpenModalShops}
                                    refs={refs}
                                    shops={shops}
                                    shopsLoaded={shopsLoaded}
                                    shopsLoading={shopsLoading}
                                    loadShops={loadShops}
                                    ready={!loading && user !== null}
                                />
                                {
                                    user && user.shop_answer !== null && user.shop_answer !== undefined && (
                                        <ShopCard userData={user} isOpen={true} openModal={openModalShops} openShop={true} setOpenModal={setOpenModalShops} shop={user.shop_answer} setOpenShop={() => {}} />
                                    )
                                }
                            </div>
                        ) : null
                    }
                </>
            ) : user && user.invoice !== undefined && user.connectwallet === undefined ? (
                <React.Suspense fallback={<LoadingBlock />}>
                    <InvoiceView user={user} />
                </React.Suspense>
            ) : user && user.connectwallet !== undefined && user.invoice === undefined && (
                <div style={{width: "100%"}}>
                    <React.Suspense fallback={<LoadingBlock />}>
                        <DisplayWallet user={user} />
                    </React.Suspense>
                </div>
            ) }
        </>
    );
}

export default App;
